import { useEffect, useState } from 'react'
import { addDataGovernor } from '../../../../api/client'
import { useCurrentUserContext } from '../../../../contexts/CurrentUserContext'
import { errorMessage } from '../DataGovernorsPage.logic'
import { PRODUCTION_ENVIRONMENT } from '../DataGovernorsPage.static'
import type { EnvironmentTab } from '../DataGovernorsPage.static'
import { localText } from './AddGovernorDialog.static'

export interface GovernorForm {
  readonly email: string
  readonly hub: string
  readonly businessObject: string
  readonly environment: string
  readonly ticketId: string
}

export interface AddGovernorOutcome {
  readonly changed: boolean
  readonly message: string
}

export interface UseAddGovernorDialogOptions {
  readonly isOpen: boolean
  readonly activeTab: EnvironmentTab
  readonly defaultHub: string
  readonly defaultBusinessObject: string
  readonly defaultEnvironment: string
  readonly onClose: () => void
  readonly onGovernorAdded: (outcome: AddGovernorOutcome) => void
}

function buildEmptyForm(defaults: Pick<
  UseAddGovernorDialogOptions,
  'defaultHub' | 'defaultBusinessObject' | 'defaultEnvironment'
>): GovernorForm {
  return {
    email: '',
    hub: defaults.defaultHub,
    businessObject: defaults.defaultBusinessObject,
    environment: defaults.defaultEnvironment,
    ticketId: '',
  }
}

export function useAddGovernorDialog({
  isOpen,
  activeTab,
  defaultHub,
  defaultBusinessObject,
  defaultEnvironment,
  onClose,
  onGovernorAdded,
}: UseAddGovernorDialogOptions) {
  const [form, setForm] = useState<GovernorForm>(() =>
    buildEmptyForm({ defaultHub, defaultBusinessObject, defaultEnvironment }),
  )
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [isMutationInProgress, setIsMutationInProgress] = useState(false)
  const { currentUser } = useCurrentUserContext()

  // Re-seed the form from the registry's current filters each time the dialog is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setForm(buildEmptyForm({ defaultHub, defaultBusinessObject, defaultEnvironment }))
      setDialogError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  function closeDialog() {
    if (!isMutationInProgress) {
      onClose()
    }
  }

  async function submit() {
    const request = {
      email: form.email.trim(),
      hub: form.hub.trim(),
      business_object: form.businessObject.trim(),
      environment: activeTab === 'prod' ? PRODUCTION_ENVIRONMENT : form.environment,
      ...(activeTab === 'prod' && { ticket_id: form.ticketId.trim() }),
      ...(currentUser?.email && { requested_by: currentUser.email }),
    }

    if (!request.email || !request.hub || !request.business_object) {
      setDialogError(localText.missingFieldsError)
      return
    }

    if (activeTab === 'prod' && !request.ticket_id) {
      setDialogError(localText.missingTicketError)
      return
    }

    setIsMutationInProgress(true)
    setDialogError(null)

    try {
      const response = await addDataGovernor(request)
      onGovernorAdded({ changed: response.changed, message: response.message })
    } catch (submitError) {
      setDialogError(errorMessage(submitError))
    } finally {
      setIsMutationInProgress(false)
    }
  }

  return { form, setForm, dialogError, isMutationInProgress, closeDialog, submit }
}
